import { Controller } from "@hotwired/stimulus"

// Manages daycare transport configuration (per-day pickup/dropoff)
export default class extends Controller {
  static targets = [
    "pickupLocation",
    "pickupDaysContainer",
    "pickupDaysGrid",
    "pickupAllDays",
    "pickupDay",
    "dropoffLocation",
    "dropoffDaysContainer",
    "dropoffDaysGrid",
    "dropoffAllDays",
    "dropoffDay"
  ]

  static values = {
    dates: Array,
    orderId: String,
    orderItemId: String
  }

  connect() {
    console.log("[DaycareTransport] Connected", {
      dates: this.datesValue,
      orderId: this.orderIdValue,
      orderItemId: this.orderItemIdValue
    })
  }

  // Called when pickup location changes
  updatePickupLocation(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]
    const locationType = selectedOption.dataset.type

    // Show/hide days selection based on location
    if (locationType === "resort" || !select.value) {
      this.pickupDaysContainerTarget.classList.add("hidden")
      // Uncheck all days when selecting resort
      this.pickupDayTargets.forEach(cb => cb.checked = false)
      this.pickupAllDaysTarget.checked = false
    } else {
      this.pickupDaysContainerTarget.classList.remove("hidden")
    }
  }

  // Called when dropoff location changes
  updateDropoffLocation(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]
    const locationType = selectedOption.dataset.type

    // Show/hide days selection based on location
    if (locationType === "resort" || !select.value) {
      this.dropoffDaysContainerTarget.classList.add("hidden")
      // Uncheck all days when selecting resort
      this.dropoffDayTargets.forEach(cb => cb.checked = false)
      this.dropoffAllDaysTarget.checked = false
    } else {
      this.dropoffDaysContainerTarget.classList.remove("hidden")
    }
  }

  // Toggle all pickup days
  toggleAllPickupDays(event) {
    const checked = event.target.checked
    this.pickupDayTargets.forEach(cb => cb.checked = checked)
  }

  // Toggle all dropoff days
  toggleAllDropoffDays(event) {
    const checked = event.target.checked
    this.dropoffDayTargets.forEach(cb => cb.checked = checked)
  }

  // Update "all days" checkbox state based on individual selections
  updatePickupDaySelection() {
    const allChecked = this.pickupDayTargets.every(cb => cb.checked)
    this.pickupAllDaysTarget.checked = allChecked
  }

  // Update "all days" checkbox state based on individual selections
  updateDropoffDaySelection() {
    const allChecked = this.dropoffDayTargets.every(cb => cb.checked)
    this.dropoffAllDaysTarget.checked = allChecked
  }

  // Get location data from select element
  getLocationData(selectTarget) {
    const select = selectTarget
    if (!select.value) return null

    const selectedOption = select.options[select.selectedIndex]
    const locationType = selectedOption.dataset.type

    if (locationType === "resort") return null

    return {
      id: select.value === "home" ? null : select.value,
      type: locationType,
      name: select.value === "home" ? "Home" : selectedOption.dataset.name
    }
  }

  // Get selected dates from checkboxes
  getSelectedDates(dayTargets) {
    return dayTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)
  }

  // Save transport configuration
  async save() {
    const pickupLocation = this.getLocationData(this.pickupLocationTarget)
    const dropoffLocation = this.getLocationData(this.dropoffLocationTarget)

    const pickupDates = pickupLocation ? this.getSelectedDates(this.pickupDayTargets) : []
    const dropoffDates = dropoffLocation ? this.getSelectedDates(this.dropoffDayTargets) : []

    const transportConfig = {
      pickup: pickupLocation ? {
        location: pickupLocation,
        dates: pickupDates
      } : null,
      dropoff: dropoffLocation ? {
        location: dropoffLocation,
        dates: dropoffDates
      } : null
    }

    console.log("[DaycareTransport] Saving config:", transportConfig)

    try {
      const response = await fetch(`/flow/booking/guest_services/save_daycare_transport?order_id=${this.orderIdValue}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          order_item_id: this.orderItemIdValue,
          daycare_transport: transportConfig
        })
      })

      if (response.ok) {
        // Close modal and refresh the transport card
        this.element.classList.add("hidden")
        document.body.classList.remove("overflow-hidden")

        // Dispatch event to update the transport summary card
        const event = new CustomEvent("daycare-transport:saved", {
          detail: transportConfig,
          bubbles: true
        })
        this.element.dispatchEvent(event)

        // Reload the page to show updated transport config
        // TODO: Replace with Turbo Stream update
        window.location.reload()
      } else {
        const error = await response.json()
        console.error("[DaycareTransport] Save failed:", error)
        alert("Failed to save transport settings. Please try again.")
      }
    } catch (error) {
      console.error("[DaycareTransport] Save error:", error)
      alert("Failed to save transport settings. Please try again.")
    }
  }
}
